// HackathonEdit.tsx - Paste your content here 
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ArrowRight, ArrowLeft as ArrowLeftIcon } from 'lucide-react';
import { hackathonAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import FormWizard from '../../components/ui/FormWizard';
import DateTimePicker from '../../components/ui/DateTimePicker';
import { formatDateTimeLocal } from '../../utils/dateUtils';

interface ProblemStatement {
  title: string;
  description: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  isHiddenFromTeams?: boolean;
}

interface Prize {
  rank: number;
  description: string;
  value: string;
}

interface Phase {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  deliverables: string[];
  status: 'upcoming' | 'active' | 'completed';
  order?: number;
}

interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  maxTeamSize: number;
  minTeamSize: number;
  maxTeams: number;
  requiresTeamApproval: boolean;
  status: string;
  isActive: boolean;
  problemStatements: ProblemStatement[];
  prizes: Prize[];
  phases: Phase[];
  rules: string[];
  requirements: string[];
  hackathonFlyer?: string;
  showInHomeCarousel?: boolean;
}

const HackathonEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hackathon, setHackathon] = useState<Hackathon | null>(null);
  const [flyerPreview, setFlyerPreview] = useState<string>('');
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  


  // Function to get signed URL for hackathon flyer
  const getSignedHackathonFlyerUrl = async (flyerUrl: string) => {
    try {
      if (!flyerUrl) return '';
      
      // If it's already a data URL (base64), return as is
      if (flyerUrl.startsWith('data:')) {
        return flyerUrl;
      }
      
      // Extract S3 key from URL, removing query parameters
      let key = flyerUrl;
      if (flyerUrl.includes('?')) {
        key = flyerUrl.split('?')[0];
      }
      
      if (key.includes('amazonaws.com')) {
        const urlParts = key.split('/');
        const bucketIndex = urlParts.findIndex(part => part.includes('.s3.') || part.includes('.s3-'));
        if (bucketIndex !== -1 && bucketIndex + 1 < urlParts.length) {
          key = urlParts.slice(bucketIndex + 1).join('/');
        }
      }
      
      console.log('🔑 Getting signed URL for hackathon flyer key:', key);
      
      const response = await fetch(`/api/upload/hackathon-flyer-url/${encodeURIComponent(key)}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Got signed hackathon flyer URL:', data.signedUrl);
        return data.signedUrl;
      } else {
        console.error('❌ Failed to get signed URL:', response.status);
        return flyerUrl; // Fallback to original URL
      }
    } catch (error) {
      console.error('❌ Error getting signed hackathon flyer URL:', error);
      return flyerUrl; // Fallback to original URL
    }
  };
  
  const handleFlyerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      error('Only JPEG, PNG, GIF and WebP images are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      error('File size must be less than 10MB');
      return;
    }

    setUploadingFlyer(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('flyer', file);

      const response = await fetch(`/api/upload/hackathon-flyer`, {
        method: 'POST',
        body: uploadFormData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      if (result.success) {
        const newFlyerUrl = result.data.flyerUrl;
        setFormData(prev => ({ ...prev, hackathonFlyer: newFlyerUrl }));
        setFlyerPreview(newFlyerUrl);
        success('Hackathon flyer uploaded successfully');
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      error('Failed to upload hackathon flyer');
    } finally {
      setUploadingFlyer(false);
    }
  };

  // Handle flyer removal
  const handleRemoveFlyer = () => {
    setFormData(prev => ({ ...prev, hackathonFlyer: '' }));
    setFlyerPreview('');
    success('Hackathon flyer removed');
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    maxTeamSize: 7,
    minTeamSize: 1,
    maxTeams: 50,
    requiresTeamApproval: true,
    status: 'upcoming',
    isActive: true,
    problemStatements: [] as ProblemStatement[],
    prizes: [] as Prize[],
    phases: [] as Phase[],
    rules: [] as string[],
    requirements: [] as string[],
    hackathonFlyer: '',
    showInHomeCarousel: false
  });

  useEffect(() => {
    const updatePreview = async () => {
      if (formData.hackathonFlyer && !formData.hackathonFlyer.startsWith('data:')) {
        const signedUrl = await getSignedHackathonFlyerUrl(formData.hackathonFlyer);
        setFlyerPreview(signedUrl);
      } else if (formData.hackathonFlyer) {
        setFlyerPreview(formData.hackathonFlyer);
      }
    };
    updatePreview();
  }, [formData.hackathonFlyer]);

  useEffect(() => {
    if (id) {
      fetchHackathon();
    }
  }, [id]);

  const fetchHackathon = async () => {
    try {
      setLoading(true);
      const response = await hackathonAPI.getHackathonById(id!);
      const hackathonData = response;
      
      setHackathon(hackathonData);
      setFormData({
        title: hackathonData.title,
        description: hackathonData.description,
        startDate: formatDateTimeLocal(hackathonData.startDate),
        endDate: formatDateTimeLocal(hackathonData.endDate),
        registrationDeadline: formatDateTimeLocal(hackathonData.registrationDeadline),
        maxTeamSize: hackathonData.maxTeamSize,
        minTeamSize: hackathonData.minTeamSize,
        maxTeams: hackathonData.maxTeams,
        requiresTeamApproval: hackathonData.requiresTeamApproval,
        status: hackathonData.status,
        isActive: hackathonData.isActive,
        problemStatements: hackathonData.problemStatements || [],
        prizes: hackathonData.prizes || [],
        phases: (hackathonData.phases || []).map((phase: any, index: number) => ({
          ...phase,
          startDate: formatDateTimeLocal(phase.startDate),
          endDate: formatDateTimeLocal(phase.endDate),
          order: phase.order || index + 1
        })),
        rules: hackathonData.rules || [],
        requirements: hackathonData.requirements || [],
        hackathonFlyer: hackathonData.hackathonFlyer || '',
        showInHomeCarousel: hackathonData.showInHomeCarousel || false
      });

      // Generate signed URL for existing flyer
      if (hackathonData.hackathonFlyer) {
        console.log('🖼️ Loading existing flyer:', hackathonData.hackathonFlyer);
        const signedUrl = await getSignedHackathonFlyerUrl(hackathonData.hackathonFlyer);
        setFlyerPreview(signedUrl);
        console.log('🔗 Flyer preview set to:', signedUrl);
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathon');
      navigate('/admin/hackathons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      // Prepare data for submission
      const submissionData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        registrationDeadline: new Date(formData.registrationDeadline).toISOString(),
        phases: formData.phases.map(phase => ({
          ...phase,
          startDate: new Date(phase.startDate).toISOString(),
          endDate: new Date(phase.endDate).toISOString()
        })),
      };

      await hackathonAPI.updateHackathon(id!, submissionData);
      success('Hackathon updated successfully');
      navigate('/admin/hackathons');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update hackathon');
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addProblemStatement = () => {
    const newProblem: ProblemStatement = {
      title: '',
      description: '',
      category: '',
      difficulty: 'medium',
      isHiddenFromTeams: false
    };
    updateFormData('problemStatements', [...formData.problemStatements, newProblem]);
  };

  const updateProblemStatement = (index: number, field: keyof ProblemStatement, value: string | boolean) => {
    const updated = [...formData.problemStatements];
    updated[index] = { ...updated[index], [field]: value };
    updateFormData('problemStatements', updated);
  };

  const removeProblemStatement = (index: number) => {
    const updated = formData.problemStatements.filter((_, i) => i !== index);
    updateFormData('problemStatements', updated);
  };

  const addPrize = () => {
    const newPrize: Prize = {
      rank: formData.prizes.length + 1,
      description: '',
      value: ''
    };
    updateFormData('prizes', [...formData.prizes, newPrize]);
  };

  const updatePrize = (index: number, field: keyof Prize, value: string | number) => {
    const updated = [...formData.prizes];
    updated[index] = { ...updated[index], [field]: value };
    updateFormData('prizes', updated);
  };

  const removePrize = (index: number) => {
    const updated = formData.prizes.filter((_, i) => i !== index);
    updateFormData('prizes', updated);
  };

  const addPhase = () => {
    const newPhase: Phase = {
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      deliverables: [],
      status: 'upcoming',
      order: formData.phases.length + 1
    };
    updateFormData('phases', [...formData.phases, newPhase]);
  };

  const updatePhase = (index: number, field: keyof Phase, value: any) => {
    const updated = [...formData.phases];
    updated[index] = { ...updated[index], [field]: value };
    updateFormData('phases', updated);
  };

  const removePhase = (index: number) => {
    const updated = formData.phases.filter((_, i) => i !== index);
    // Reorder the remaining phases
    const reordered = updated.map((phase, i) => ({
      ...phase,
      order: i + 1
    }));
    updateFormData('phases', reordered);
  };

  const addDeliverable = (phaseIndex: number) => {
    const updated = [...formData.phases];
    updated[phaseIndex].deliverables.push('');
    updateFormData('phases', updated);
  };

  const updateDeliverable = (phaseIndex: number, deliverableIndex: number, value: string) => {
    const updated = [...formData.phases];
    updated[phaseIndex].deliverables[deliverableIndex] = value;
    updateFormData('phases', updated);
  };

  const removeDeliverable = (phaseIndex: number, deliverableIndex: number) => {
    const updated = [...formData.phases];
    updated[phaseIndex].deliverables.splice(deliverableIndex, 1);
    updateFormData('phases', updated);
  };

  const addRule = () => {
    updateFormData('rules', [...formData.rules, '']);
  };

  const updateRule = (index: number, value: string) => {
    const updated = [...formData.rules];
    updated[index] = value;
    updateFormData('rules', updated);
  };

  const removeRule = (index: number) => {
    const updated = formData.rules.filter((_, i) => i !== index);
    updateFormData('rules', updated);
  };

  const addRequirement = () => {
    updateFormData('requirements', [...formData.requirements, '']);
  };

  const updateRequirement = (index: number, value: string) => {
    const updated = [...formData.requirements];
    updated[index] = value;
    updateFormData('requirements', updated);
  };

  const removeRequirement = (index: number) => {
    const updated = formData.requirements.filter((_, i) => i !== index);
    updateFormData('requirements', updated);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!hackathon) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">Hackathon not found</div>
      </div>
    );
  }

  const steps = [
    {
      title: 'Basic Information',
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => updateFormData('title', e.target.value)}
              placeholder="Enter hackathon title"
              maxLength={100}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.title.length}/100 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => updateFormData('description', e.target.value)}
              placeholder="Describe the hackathon"
              rows={4}
              maxLength={2000}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length}/2000 characters
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <DateTimePicker
                value={formData.startDate}
                onChange={(value) => updateFormData('startDate', value)}
                placeholder="Start date and time"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <DateTimePicker
                value={formData.endDate}
                onChange={(value) => updateFormData('endDate', value)}
                placeholder="End date and time"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Deadline *
              </label>
              <DateTimePicker
                value={formData.registrationDeadline}
                onChange={(value) => updateFormData('registrationDeadline', value)}
                placeholder="Registration deadline"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Team Size *
              </label>
              <Input
                type="number"
                value={formData.minTeamSize}
                onChange={(e) => updateFormData('minTeamSize', parseInt(e.target.value))}
                min={1}
                max={5}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Team Size *
              </label>
              <Input
                type="number"
                value={formData.maxTeamSize}
                onChange={(e) => updateFormData('maxTeamSize', parseInt(e.target.value))}
                min={formData.minTeamSize}
                max={10}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Teams *
              </label>
              <Input
                type="number"
                value={formData.maxTeams}
                onChange={(e) => updateFormData('maxTeams', parseInt(e.target.value))}
                min={1}
                max={1000}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => updateFormData('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="upcoming">Upcoming</option>
                <option value="registration_open">Registration Open</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="requiresTeamApproval"
                checked={formData.requiresTeamApproval}
                onChange={(e) => updateFormData('requiresTeamApproval', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="requiresTeamApproval" className="text-sm font-medium text-gray-700">
                Requires Team Approval
              </label>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => updateFormData('isActive', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Active
            </label>
          </div>

          {/* Hackathon Flyer Section */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900">Hackathon Flyer</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hackathon Flyer
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFlyerUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload a flyer image (JPEG, PNG, GIF, WebP - Max 10MB)
              </p>
              {uploadingFlyer && (
                <div className="flex items-center space-x-2 mt-2">
                  <LoadingSpinner className="w-4 h-4" />
                  <span className="text-sm text-gray-600">Uploading...</span>
                </div>
              )}
              {flyerPreview && !uploadingFlyer && (
                <div className="mt-2">
                  <img
                    src={flyerPreview}
                    alt="Hackathon flyer preview"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    onError={(e) => console.error('❌ Failed to load flyer preview image:', (e.target as HTMLImageElement)?.src)}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveFlyer}
                    className="text-red-600 hover:text-red-700 text-sm mt-2 underline"
                  >
                    Remove Flyer
                  </button>
                </div>
              )}
              {flyerPreview && (
                <Button
                  type="button"
                  onClick={handleRemoveFlyer}
                  className="text-red-600 hover:text-red-700 text-sm mt-2"
                  variant="outline"
                >
                  Remove Flyer
                </Button>
              )}
            </div>

            {/* Show in Home Carousel Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showInHomeCarousel"
                checked={formData.showInHomeCarousel}
                onChange={(e) => updateFormData('showInHomeCarousel', e.target.checked)}
                disabled={!flyerPreview && !formData.hackathonFlyer}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="showInHomeCarousel" className="text-sm font-medium text-gray-700">
                Show hackathon flyer in home page carousel
                {!flyerPreview && !formData.hackathonFlyer && (
                  <span className="text-gray-500 ml-1">(Upload a flyer first to enable this option)</span>
                )}
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Phases',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Hackathon Phases</h3>
            <Button
              type="button"
              onClick={addPhase}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add Phase
            </Button>
          </div>

          {formData.phases.map((phase, index) => (
            <Card key={index} className="border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Phase {index + 1}</h4>
                  <Button
                    type="button"
                    onClick={() => removePhase(index)}
                    className="text-red-600 hover:text-red-700"
                    variant="outline"
                    size="sm"
                  >
                    Remove
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phase Name *
                    </label>
                    <Input
                      type="text"
                      value={phase.name}
                      onChange={(e) => updatePhase(index, 'name', e.target.value)}
                      placeholder="e.g., Ideation, Development, Submission"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      value={phase.status}
                      onChange={(e) => updatePhase(index, 'status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="upcoming">Upcoming</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <Textarea
                    value={phase.description}
                    onChange={(e) => updatePhase(index, 'description', e.target.value)}
                    placeholder="Describe this phase"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <DateTimePicker
                      value={phase.startDate}
                      onChange={(value) => updatePhase(index, 'startDate', value)}
                      placeholder="Phase start date and time"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <DateTimePicker
                      value={phase.endDate}
                      onChange={(value) => updatePhase(index, 'endDate', value)}
                      placeholder="Phase end date and time"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Deliverables
                    </label>
                    <Button
                      type="button"
                      onClick={() => addDeliverable(index)}
                      className="text-blue-600 hover:text-blue-700"
                      variant="outline"
                      size="sm"
                    >
                      Add Deliverable
                    </Button>
                  </div>
                  {phase.deliverables.map((deliverable, deliverableIndex) => (
                    <div key={deliverableIndex} className="flex gap-2 mb-2">
                      <Input
                        type="text"
                        value={deliverable}
                        onChange={(e) => updateDeliverable(index, deliverableIndex, e.target.value)}
                        placeholder="e.g., Project proposal, Demo video"
                      />
                      <Button
                        type="button"
                        onClick={() => removeDeliverable(index, deliverableIndex)}
                        className="text-red-600 hover:text-red-700"
                        variant="outline"
                        size="sm"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {formData.phases.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No phases added yet. Click "Add Phase" to get started.
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Problem Statements',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Problem Statements</h3>
            <Button
              type="button"
              onClick={addProblemStatement}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add Problem Statement
            </Button>
          </div>

          {formData.problemStatements.map((problem, index) => (
            <Card key={index} className="border-l-4 border-l-green-500">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Problem {index + 1}</h4>
                  <Button
                    type="button"
                    onClick={() => removeProblemStatement(index)}
                    className="text-red-600 hover:text-red-700"
                    variant="outline"
                    size="sm"
                  >
                    Remove
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <Input
                      type="text"
                      value={problem.title}
                      onChange={(e) => updateProblemStatement(index, 'title', e.target.value)}
                      placeholder="Enter problem title"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty *
                    </label>
                    <select
                      value={problem.difficulty}
                      onChange={(e) => updateProblemStatement(index, 'difficulty', e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <Input
                    type="text"
                    value={problem.category}
                    onChange={(e) => updateProblemStatement(index, 'category', e.target.value)}
                    placeholder="e.g., Web Development, AI/ML, Mobile"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <Textarea
                    value={problem.description}
                    onChange={(e) => updateProblemStatement(index, 'description', e.target.value)}
                    placeholder="Describe the problem in detail"
                    rows={4}
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`hiddenFromTeams-${index}`}
                    checked={problem.isHiddenFromTeams || false}
                    onChange={(e) => updateProblemStatement(index, 'isHiddenFromTeams', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`hiddenFromTeams-${index}`} className="text-sm font-medium text-gray-700">
                    Hide from team creation (problem statement will not be available for teams to select)
                  </label>
                </div>
              </CardContent>
            </Card>
          ))}

          {formData.problemStatements.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No problem statements added yet. Click "Add Problem Statement" to get started.
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Prizes',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Prizes</h3>
            <Button
              type="button"
              onClick={addPrize}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add Prize
            </Button>
          </div>

          {formData.prizes.map((prize, index) => (
            <Card key={index} className="border-l-4 border-l-yellow-500">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    {prize.rank === 1 ? '🥇' : prize.rank === 2 ? '🥈' : prize.rank === 3 ? '🥉' : `${prize.rank}th`} Place
                  </h4>
                  <Button
                    type="button"
                    onClick={() => removePrize(index)}
                    className="text-red-600 hover:text-red-700"
                    variant="outline"
                    size="sm"
                  >
                    Remove
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rank *
                    </label>
                    <Input
                      type="number"
                      value={prize.rank}
                      onChange={(e) => updatePrize(index, 'rank', parseInt(e.target.value))}
                      min={1}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Value *
                    </label>
                    <Input
                      type="text"
                      value={prize.value}
                      onChange={(e) => updatePrize(index, 'value', e.target.value)}
                      placeholder="e.g., $1000, Laptop, Internship"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <Textarea
                    value={prize.description}
                    onChange={(e) => updatePrize(index, 'description', e.target.value)}
                    placeholder="Describe the prize"
                    rows={3}
                    required
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          {formData.prizes.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No prizes added yet. Click "Add Prize" to get started.
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Rules',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Rules</h3>
            <Button
              type="button"
              onClick={addRule}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add Rule
            </Button>
          </div>

          {formData.rules.map((rule, index) => (
            <div key={index} className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="text"
                  value={rule}
                  onChange={(e) => updateRule(index, e.target.value)}
                  placeholder="Enter a rule"
                  required
                />
              </div>
              <Button
                type="button"
                onClick={() => removeRule(index)}
                className="text-red-600 hover:text-red-700"
                variant="outline"
                size="sm"
              >
                Remove
              </Button>
            </div>
          ))}

          {formData.rules.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No rules added yet. Click "Add Rule" to get started.
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Requirements',
      content: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Requirements</h3>
            <Button
              type="button"
              onClick={addRequirement}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add Requirement
            </Button>
          </div>

          {formData.requirements.map((requirement, index) => (
            <div key={index} className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="text"
                  value={requirement}
                  onChange={(e) => updateRequirement(index, e.target.value)}
                  placeholder="Enter a requirement"
                  required
                />
              </div>
              <Button
                type="button"
                onClick={() => removeRequirement(index)}
                className="text-red-600 hover:text-red-700"
                variant="outline"
                size="sm"
              >
                Remove
              </Button>
            </div>
          ))}

          {formData.requirements.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No requirements added yet. Click "Add Requirement" to get started.
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Review',
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Review Your Hackathon</h3>
            <p className="text-blue-700">
              Please review all the information before saving. You can go back to any step to make changes.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">{formData.title}</h4>
                <p className="text-gray-600">{formData.description}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Start Date:</span> {formData.startDate}
                </div>
                <div>
                  <span className="font-medium">End Date:</span> {formData.endDate}
                </div>
                <div>
                  <span className="font-medium">Registration Deadline:</span> {formData.registrationDeadline}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Team Size:</span> {formData.minTeamSize}-{formData.maxTeamSize}
                </div>
                <div>
                  <span className="font-medium">Max Teams:</span> {formData.maxTeams}
                </div>
                <div>
                  <span className="font-medium">Status:</span> 
                  <Badge className={`ml-2 ${formData.status === 'upcoming' ? 'bg-blue-100 text-blue-800' : 
                    formData.status === 'registration_open' ? 'bg-green-100 text-green-800' : 
                    formData.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-gray-100 text-gray-800'}`}>
                    {formData.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Phases ({formData.phases.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {formData.phases.map((phase, index) => (
                <div key={index} className="mb-4 p-3 bg-gray-50 rounded">
                  <h5 className="font-medium">{phase.name}</h5>
                  <p className="text-sm text-gray-600">{phase.description}</p>
                  <p className="text-xs text-gray-500">{phase.startDate} - {phase.endDate}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Problem Statements ({formData.problemStatements.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {formData.problemStatements.map((problem, index) => (
                <div key={index} className="mb-4 p-3 bg-gray-50 rounded">
                  <h5 className="font-medium">{problem.title}</h5>
                  <p className="text-sm text-gray-600">{problem.description}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge className="bg-blue-100 text-blue-800">{problem.category}</Badge>
                    <Badge className="bg-green-100 text-green-800">{problem.difficulty}</Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Prizes ({formData.prizes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {formData.prizes.map((prize, index) => (
                <div key={index} className="mb-4 p-3 bg-gray-50 rounded">
                  <h5 className="font-medium">{prize.rank === 1 ? '🥇' : prize.rank === 2 ? '🥈' : prize.rank === 3 ? '🥉' : `${prize.rank}th`} Place</h5>
                  <p className="text-sm text-gray-600">{prize.description}</p>
                  <p className="text-sm font-medium text-green-600">{prize.value}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Rules ({formData.rules.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1">
                {formData.rules.map((rule, index) => (
                  <li key={index} className="text-sm text-gray-600">{rule}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Requirements ({formData.requirements.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1">
                {formData.requirements.map((requirement, index) => (
                  <li key={index} className="text-sm text-gray-600">{requirement}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/hackathons')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Hackathons
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Hackathon</h1>
        <p className="text-gray-600">Update hackathon details and configuration</p>
      </div>

      <FormWizard
        steps={steps}
        onComplete={handleSubmit}
        loading={saving}
        submitText="Update Hackathon"
        submitIcon={<Save className="h-4 w-4" />}
      />
    </div>
  );
};

export default HackathonEdit;